/**
 * TRH Brand Browser - Off-Site Brand Browsing Module
 * ===========================================================================
 * Provides a seamless experience for browsing external fabric/trim/wallpaper
 * vendor websites without leaving The ReBorn House.
 *
 * Two modes:
 *   1. Iframe modal — for the few vendors that allow embedding
 *   2. Popup window — for vendors that block iframes (majority)
 *
 * The parent page always stays on therebornhouse.com. When using popup mode,
 * a branded overlay displays on the parent page until the popup is closed.
 *
 * @module TRHBrandBrowser
 * @version 1.0.0
 *
 * Usage:
 *   TRHBrandBrowser.init({ containerSelector: '#trh-bb-grid' });
 *
 * HTML expected:
 *   <div class="trh-bb-link" data-href="https://..." data-name="Brand Name">
 *     <img src="..." alt="Brand Name">
 *   </div>
 *
 *   Add data-iframe-ok="true" on brands known to allow iframe embedding.
 */
(function () {
  'use strict';

  /* =========================================================================
   * CONSTANTS
   * ========================================================================= */

  /**
   * Domains known to allow iframe embedding (verified April 2025).
   * Used as a fallback check when data-iframe-ok is not set.
   * @const {string[]}
   */
  var IFRAME_ALLOWED = [
    'schumacher.com',
    'thibautdesign.com',
    'fortuny.com',
    'zakandfox.com'
  ];

  /** @const {number} Interval in ms to poll for popup window close. */
  var POLL_INTERVAL = 500;

  /* =========================================================================
   * MODULE STATE
   * ========================================================================= */

  /** @type {Window|null} Reference to the open popup window. */
  var _popupRef = null;

  /** @type {Window|null} Reference to the small overlay popup. */
  var _overlayRef = null;

  /** @type {number|null} setInterval ID for polling popup.closed. */
  var _pollTimer = null;

  /** @type {string|null} Currently browsing brand name. */
  var _currentBrand = null;

  /** @type {boolean} Whether touch device (affects messaging). */
  var _isTouch = false;

  /** @type {Object} References to DOM elements. */
  var _els = {};

  /* =========================================================================
   * UTILITIES
   * ========================================================================= */

  /**
   * Extract hostname from a URL string.
   * @param {string} url
   * @returns {string} Hostname (e.g. "www.kravet.com")
   */
  function getHostname(url) {
    try {
      return new URL(url).hostname;
    } catch (e) {
      return '';
    }
  }

  /**
   * Check if a URL's domain is in the iframe-allowed list.
   * @param {string} url
   * @returns {boolean}
   */
  function canIframe(url) {
    var hostname = getHostname(url).toLowerCase();
    for (var i = 0; i < IFRAME_ALLOWED.length; i++) {
      if (hostname.indexOf(IFRAME_ALLOWED[i]) !== -1) return true;
    }
    return false;
  }

  /* =========================================================================
   * DOM CREATION
   * ========================================================================= */

  /**
   * Create the iframe modal element and append to document.body.
   */
  function createIframeModal() {
    var overlay = document.createElement('div');
    overlay.className = 'trh-bb-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Brand site viewer');

    overlay.innerHTML =
      '<div class="trh-bb-modal">' +
        '<div class="trh-bb-modal-header">' +
          '<span class="trh-bb-modal-title"></span>' +
          '<div class="trh-bb-modal-actions">' +
            '<a class="trh-bb-btn trh-bb-newtab" href="#" target="_blank" rel="noopener">\u2197 Open in new tab</a>' +
            '<button type="button" class="trh-bb-modal-close" aria-label="Close">\u2715</button>' +
          '</div>' +
        '</div>' +
        '<div class="trh-bb-blocked">' +
          '<div class="trh-bb-blocked-icon">\uD83E\uDDF5</div>' +
          '<p class="trh-bb-blocked-heading"></p>' +
          '<p class="trh-bb-blocked-msg"></p>' +
          '<div class="trh-bb-blocked-actions">' +
            '<a class="trh-bb-blocked-link" href="#" target="_blank" rel="noopener">Browse their site \u2197</a>' +
            '<p class="trh-bb-blocked-return">When you find something you love, come back and<br>let us know \u2014 we\'ll source it for you!</p>' +
          '</div>' +
        '</div>' +
        '<iframe class="trh-bb-modal-iframe" title="Brand website" sandbox="allow-scripts allow-same-origin allow-forms allow-popups"></iframe>' +
      '</div>';

    document.body.appendChild(overlay);

    _els.iframeOverlay = overlay;
    _els.iframeModal = overlay.querySelector('.trh-bb-modal');
    _els.iframeTitle = overlay.querySelector('.trh-bb-modal-title');
    _els.iframeNewTab = overlay.querySelector('.trh-bb-newtab');
    _els.iframeClose = overlay.querySelector('.trh-bb-modal-close');
    _els.iframe = overlay.querySelector('.trh-bb-modal-iframe');
    _els.blocked = overlay.querySelector('.trh-bb-blocked');
    _els.blockedHeading = overlay.querySelector('.trh-bb-blocked-heading');
    _els.blockedMsg = overlay.querySelector('.trh-bb-blocked-msg');
    _els.blockedLink = overlay.querySelector('.trh-bb-blocked-link');

    _els.iframeClose.addEventListener('click', closeIframeModal);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeIframeModal();
    });
  }

  /**
   * Create the popup browsing overlay and append to document.body.
   */
  function createPopupOverlay() {
    var overlay = document.createElement('div');
    overlay.className = 'trh-bb-popup-overlay';

    overlay.innerHTML =
      '<div class="trh-bb-popup-card">' +
        '<div class="trh-bb-popup-header">The ReBorn House</div>' +
        '<div class="trh-bb-popup-body">' +
          '<p class="trh-bb-popup-label"><span class="trh-bb-popup-pulse"></span>Currently browsing</p>' +
          '<p class="trh-bb-popup-brand"></p>' +
          '<p class="trh-bb-popup-instructions"></p>' +
          '<button type="button" class="trh-bb-popup-goto-btn">Go to Brand \u2197</button>' +
          '<button type="button" class="trh-bb-popup-close-btn">Close &amp; Return</button>' +
          '<p class="trh-bb-popup-hint"></p>' +
        '</div>' +
      '</div>';

    document.body.appendChild(overlay);

    _els.popupOverlay = overlay;
    _els.popupBrand = overlay.querySelector('.trh-bb-popup-brand');
    _els.popupInstructions = overlay.querySelector('.trh-bb-popup-instructions');
    _els.popupGotoBtn = overlay.querySelector('.trh-bb-popup-goto-btn');
    _els.popupCloseBtn = overlay.querySelector('.trh-bb-popup-close-btn');
    _els.popupHint = overlay.querySelector('.trh-bb-popup-hint');

    _els.popupGotoBtn.addEventListener('click', function () {
      if (_popupRef && !_popupRef.closed) {
        _popupRef.focus();
      }
    });
    _els.popupCloseBtn.addEventListener('click', closePopup);
  }

  /* =========================================================================
   * IFRAME MODAL
   * ========================================================================= */

  /**
   * Open a vendor site in the iframe modal.
   * @param {string} url  - Vendor URL.
   * @param {string} name - Brand display name.
   */
  function openIframeModal(url, name) {
    _els.iframeTitle.textContent = name;
    _els.iframeNewTab.href = url;
    _els.blockedLink.href = url;
    _els.blockedHeading.textContent = name + " can\u2019t be previewed here";
    _els.blockedMsg.textContent =
      "Their website doesn\u2019t allow previews \u2014 but that\u2019s okay! " +
      "Click below to browse their fabrics in a new tab, note anything you love, " +
      "then come back and tell us. We\u2019ll source it for you!";

    _els.blocked.classList.remove('active');
    _els.iframe.style.display = 'block';
    _els.iframe.src = url;

    _els.iframeOverlay.classList.add('open');
    document.body.style.overflow = 'hidden';

    _els.iframe.onload = function () {
      try {
        var doc = _els.iframe.contentDocument;
        if (doc && (doc.URL === 'about:blank' || doc.body.innerHTML === '')) {
          showBlocked();
        }
      } catch (e) {
        /* Cross-origin = loaded fine */
      }
    };
  }

  /**
   * Show the "blocked" fallback inside the iframe modal.
   */
  function showBlocked() {
    _els.iframe.style.display = 'none';
    _els.blocked.classList.add('active');
  }

  /**
   * Close the iframe modal.
   */
  function closeIframeModal() {
    _els.iframeOverlay.classList.remove('open');
    _els.iframe.src = 'about:blank';
    document.body.style.overflow = '';
  }

  /* =========================================================================
   * POPUP WINDOW
   * ========================================================================= */

  /**
   * Open a vendor site in a popup window with parent overlay.
   * @param {string} url  - Vendor URL.
   * @param {string} name - Brand display name.
   */
  /**
   * Build HTML for the small overlay popup window.
   * @param {string} name - Brand display name.
   * @returns {string}
   */
  function buildOverlayHTML(name) {
    return '<!DOCTYPE html><html><head><title>The ReBorn House</title>' +
      '<style>' +
      '*{box-sizing:border-box;margin:0;padding:0}' +
      'body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;overflow:hidden}' +
      '.hdr{background:#7C8C74;color:#fff;padding:12px 16px;font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;text-align:center}' +
      '.bd{padding:24px 20px;text-align:center}' +
      '.lbl{font-size:11px;color:#888;text-transform:uppercase;letter-spacing:.08em;margin-bottom:4px}' +
      '.dot{display:inline-block;width:7px;height:7px;background:#7C8C74;border-radius:50%;margin-right:6px;vertical-align:middle;animation:pulse 2s ease-in-out infinite}' +
      '@keyframes pulse{0%,100%{opacity:.4;transform:scale(.9)}50%{opacity:1;transform:scale(1.1)}}' +
      '.brand{font-size:20px;font-weight:700;color:#2e2e2e;margin:6px 0 18px;font-family:"Times New Roman",Garamond,serif}' +
      '.msg{font-size:12px;color:#666;line-height:1.5;margin-bottom:18px}' +
      '.btn{display:block;width:100%;padding:11px 12px;font-size:13px;font-weight:600;border-radius:6px;cursor:pointer;border:none;margin-bottom:8px;font-family:inherit;transition:background .2s,color .2s}' +
      '.go{background:#fff;color:#7C8C74;border:2px solid #7C8C74}' +
      '.go:hover{background:#7C8C74;color:#fff}' +
      '.close{background:#7C8C74;color:#fff;border:2px solid #7C8C74}' +
      '.close:hover{background:#5e6e58;border-color:#5e6e58}' +
      '</style></head><body>' +
      '<div class="hdr">The ReBorn House</div>' +
      '<div class="bd">' +
      '<p class="lbl"><span class="dot"></span>Currently browsing</p>' +
      '<p class="brand">' + name.replace(/</g, '&lt;') + '</p>' +
      '<p class="msg">Find something you love, then come back and let us know!</p>' +
      '<button class="btn go" onclick="try{var w=window.open(\'\',\'trh_brand\');if(w)w.focus();}catch(e){}">Go to ' + name.replace(/</g, '&lt;') + ' \u2197</button>' +
      '<button class="btn close" onclick="try{window.opener.TRHBrandBrowser.closeAll();}catch(e){}window.close();">Close &amp; Return</button>' +
      '</div></body></html>';
  }

  /** @type {boolean} True if browser is Safari. */
  var _isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  function openPopupWindow(url, name) {
    _currentBrand = name;

    if (_isSafari) {
      openPopupSafari(url, name);
    } else {
      openPopupChrome(url, name);
    }
  }

  /**
   * Chrome/Firefox: two popup windows side by side.
   * Vendor on the left, "Close & Return" overlay pinned to the right edge of the screen.
   */
  function openPopupChrome(url, name) {
    var ow = 300;   /* overlay width */
    var oh = 340;   /* overlay height */
    var gap = 20;   /* space between the two popups */

    /* Overlay position: flush to right edge of screen, vertically centered */
    var oleft = screen.availWidth - ow - 10;
    var otop = Math.round((screen.availHeight - oh) / 2);

    /* Vendor popup: left side, sized to fill space left of the overlay */
    var w = screen.availWidth - ow - gap - 20;
    if (w > 1000) w = 1000;
    var h = Math.round(screen.availHeight * 0.82);
    if (h > 750) h = 750;
    var left = 10;
    var top = Math.round((screen.availHeight - h) / 2);

    /* Open vendor first */
    var features =
      'width=' + w + ',height=' + h +
      ',left=' + left + ',top=' + top +
      ',resizable=yes,scrollbars=yes,location=yes';

    _popupRef = window.open('about:blank', 'trh_brand', features);
    if (_popupRef && !_popupRef.closed) {
      try { _popupRef.location.replace(url); } catch (e) { _popupRef.location = url; }
    }
    if (!_popupRef || _popupRef.closed) {
      _popupRef = null;
      showPopupBlocked(url, name);
      return;
    }

    /* Open overlay popup SECOND — so it renders on top of the vendor popup */
    var ofeatures =
      'width=' + ow + ',height=' + oh +
      ',left=' + oleft + ',top=' + otop +
      ',resizable=no,scrollbars=no,menubar=no,toolbar=no,location=no,status=no';

    _overlayRef = window.open('', 'trh_overlay', ofeatures);
    if (_overlayRef && !_overlayRef.closed) {
      _overlayRef.document.write(buildOverlayHTML(name));
      _overlayRef.document.close();
      /* Force focus on overlay so it stays on top */
      _overlayRef.focus();
    } else {
      _overlayRef = null;
      showPopupOverlay(name);
    }

    startPollTimer();
  }

  /**
   * Safari: single vendor popup + DOM overlay on the parent page.
   * Safari ignores popup window positioning, so we skip the second
   * popup entirely and use a DOM card on the parent page instead.
   */
  function openPopupSafari(url, name) {
    var w = Math.round(screen.availWidth * 0.6);
    var h = Math.round(screen.availHeight * 0.68);
    var left = 10;
    var top = Math.round((screen.availHeight - h) / 2);

    var features =
      'width=' + w + ',height=' + h +
      ',left=' + left + ',top=' + top +
      ',resizable=yes,scrollbars=yes,location=yes';

    _popupRef = window.open('about:blank', 'trh_brand', features);
    if (_popupRef && !_popupRef.closed) {
      try { _popupRef.location.replace(url); } catch (e) { _popupRef.location = url; }
    }
    if (!_popupRef || _popupRef.closed) {
      _popupRef = null;
      showPopupBlocked(url, name);
      return;
    }

    /* DOM overlay on the parent page — card pinned to right center */
    _overlayRef = null;
    showPopupOverlay(name);
    startPollTimer();
  }

  /**
   * Poll for popup close — works for both Chrome and Safari paths.
   */
  function startPollTimer() {
    _pollTimer = setInterval(function () {
      var vendorClosed = !_popupRef || _popupRef.closed;
      var overlayClosed = _overlayRef && _overlayRef.closed;

      if (vendorClosed || overlayClosed) {
        clearInterval(_pollTimer);
        _pollTimer = null;
        if (_popupRef && !_popupRef.closed) {
          try { _popupRef.close(); } catch (e) { /* cross-origin */ }
        }
        if (_overlayRef && !_overlayRef.closed) {
          try { _overlayRef.close(); } catch (e) { /* cross-origin */ }
        }
        _popupRef = null;
        _overlayRef = null;
        hidePopupOverlay();
      }
    }, POLL_INTERVAL);
  }

  /**
   * Display the parent page overlay while popup is open.
   * @param {string} name - Brand name.
   */
  function showPopupOverlay(name) {
    _els.popupBrand.textContent = name;
    _els.popupGotoBtn.textContent = 'Go to ' + name + ' \u2197';

    if (_isTouch) {
      _els.popupInstructions.textContent =
        'Their site opened in a new tab. Find something you love, then come back here!';
      _els.popupHint.textContent = 'Switch back to this tab when you\'re done browsing.';
    } else {
      _els.popupInstructions.textContent =
        'Their site is open in a popup window. Find something you love, then close the popup to return!';
      _els.popupHint.textContent = '';
    }

    _els.popupOverlay.classList.add('open');
  }

  /**
   * Hide the popup overlay and restore scroll.
   */
  function hidePopupOverlay() {
    _els.popupOverlay.classList.remove('open');
    _currentBrand = null;
  }

  /**
   * Close the popup window and overlay.
   */
  function closePopup() {
    if (_pollTimer) {
      clearInterval(_pollTimer);
      _pollTimer = null;
    }
    if (_popupRef && !_popupRef.closed) {
      try { _popupRef.close(); } catch (e) { /* cross-origin */ }
    }
    if (_overlayRef && !_overlayRef.closed) {
      try { _overlayRef.close(); } catch (e) { /* cross-origin */ }
    }
    _popupRef = null;
    _overlayRef = null;
    hidePopupOverlay();
  }

  /**
   * Handle the case when the browser blocks the popup.
   * Falls back to the iframe modal with the blocked message.
   * @param {string} url  - Vendor URL.
   * @param {string} name - Brand name.
   */
  function showPopupBlocked(url, name) {
    _els.iframeTitle.textContent = name;
    _els.iframeNewTab.href = url;
    _els.blockedLink.href = url;
    _els.blockedHeading.textContent = 'Popup was blocked';
    _els.blockedMsg.textContent =
      'Your browser blocked the popup window. Click below to open ' +
      name + "'s website in a new tab instead. Note anything you love, " +
      "then come back and tell us \u2014 we\u2019ll source it for you!";

    _els.iframe.style.display = 'none';
    _els.blocked.classList.add('active');
    _els.iframeOverlay.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  /* =========================================================================
   * MAIN ENTRY POINT
   * ========================================================================= */

  /**
   * Open a brand site using the appropriate method.
   * @param {string}  url       - Vendor URL.
   * @param {string}  name      - Brand display name.
   * @param {boolean} iframeOk  - Whether this brand allows iframe embedding.
   */
  function openBrand(url, name, iframeOk) {
    if (iframeOk || canIframe(url)) {
      openIframeModal(url, name);
    } else {
      openPopupWindow(url, name);
    }
  }

  /**
   * Close everything — iframe modal, popup, overlay.
   */
  function closeAll() {
    if (_els.iframeOverlay && _els.iframeOverlay.classList.contains('open')) {
      closeIframeModal();
    }
    if (_els.popupOverlay && _els.popupOverlay.classList.contains('open')) {
      closePopup();
    }
  }

  /* =========================================================================
   * INITIALIZATION
   * ========================================================================= */

  /**
   * Initialize the brand browser.
   * @param {Object} config
   * @param {string} config.containerSelector - CSS selector for the brand grid container.
   */
  function init(config) {
    config = config || {};
    var containerSelector = config.containerSelector || '.trh-bb-grid';

    _isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

    /* Create DOM elements */
    createIframeModal();
    createPopupOverlay();

    /* Bind click handlers on all brand links */
    var containers = document.querySelectorAll(containerSelector);
    for (var c = 0; c < containers.length; c++) {
      var links = containers[c].querySelectorAll('.trh-bb-link[data-href]');
      for (var i = 0; i < links.length; i++) {
        (function (el) {
          el.addEventListener('click', function (e) {
            e.preventDefault();
            var url = el.getAttribute('data-href');
            var name = el.getAttribute('data-name') || 'Brand';
            var iframeOk = el.getAttribute('data-iframe-ok') === 'true';
            openBrand(url, name, iframeOk);
          });
        })(links[i]);
      }
    }

    /* Escape key closes everything */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });
  }

  /* =========================================================================
   * PUBLIC API
   * ========================================================================= */

  window.TRHBrandBrowser = {
    init: init,
    openBrand: openBrand,
    closeAll: closeAll
  };

})();
